import React from "react";
import { Plus, Columns3, Download, RefreshCw, ChevronDown, Upload, FileDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem } from "@/components/ui/dropdown-menu";

interface TablePageHeaderProps {
  title: string;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onAddNew?: () => void;
  addButtonText?: string;
  children?: React.ReactNode;
  columns?: any[];
  visibleColumns?: string[];
  onColumnVisibilityChange?: (columns: string[]) => void;
  onRefresh?: () => void;
  onImport?: () => void;
  onExport?: () => void;
  onExportTemplate?: () => void;
}

export const TablePageHeader: React.FC<TablePageHeaderProps> = ({
  title,
  searchQuery,
  onSearchChange,
  onAddNew,
  addButtonText = "Add New",
  children,
  columns = [],
  visibleColumns = [],
  onColumnVisibilityChange,
  onRefresh,
  onImport,
  onExport,
  onExportTemplate
}) => {
  const handleColumnToggle = (columnKey: string) => {
    if (onColumnVisibilityChange) {
      const newVisibleColumns = visibleColumns.includes(columnKey)
        ? visibleColumns.filter(key => key !== columnKey)
        : [...visibleColumns, columnKey];
      onColumnVisibilityChange(newVisibleColumns);
    }
  };
  return (
    <div className="table-page-header pt-4">
      <div className="flex items-center gap-3">
        <div className="relative min-w-[300px] pl-4">
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        
        {children}
        
        {/* Columns button */}
        {columns.length > 0 && onColumnVisibilityChange && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <Columns3 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {columns.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.key}
                  checked={visibleColumns.includes(column.key)}
                  onCheckedChange={() => handleColumnToggle(column.key)}
                >
                  {column.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Export buttons */}
        {(onExport || onImport || onExportTemplate) && (
          <div className="flex items-center">
            {onExport && (
              <Button variant="outline" size="icon" onClick={onExport} className="rounded-r-none border-r-0">
                <Download className="h-4 w-4" />
              </Button>
            )}
            {(onImport || onExportTemplate) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className={onExport ? "rounded-l-none" : ""}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {onImport && (
                    <DropdownMenuItem onClick={onImport}>
                      <Upload className="h-4 w-4 mr-2" />
                      Import
                    </DropdownMenuItem>
                  )}
                  {onExportTemplate && (
                    <DropdownMenuItem onClick={onExportTemplate}>
                      <FileDown className="h-4 w-4 mr-2" />
                      Template
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}

        {/* Refresh button */}
        {onRefresh && (
          <Button variant="outline" size="icon" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        )}

        {/* Add new button */}
        {onAddNew && (
          <Button onClick={onAddNew} size="icon">
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};